import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, Calendar, Eye, Filter, MessageSquare, ArrowLeft, User, X } from 'lucide-react';
import { contactoService, type ContactoMensaje } from '../../services/contacto/contacto.service';
import { useContactNotificationsStore } from '../../store/contact-notifications.store';
import { NotificationToast } from '../../components/ui/NotificationToast';

type FiltroEstado = 'todos' | 'nuevo' | 'leido';

export const DashboardContactosPage: React.FC = () => {
  const navigate = useNavigate();
  const [mensajes, setMensajes] = useState<ContactoMensaje[]>([]);
  const [filtro, setFiltro] = useState<FiltroEstado>('todos');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMensaje, setSelectedMensaje] = useState<ContactoMensaje | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const decrementNuevos = useContactNotificationsStore((state) => state.decrementNuevos);
  const refreshStats = useContactNotificationsStore((state) => state.refreshStats);
  const [toast, setToast] = useState<{ isOpen: boolean; title: string; message: string; type: 'success' | 'error' | 'info' }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  const showToast = (title: string, message: string, type: 'success' | 'error' | 'info') => {
    setToast({ isOpen: true, title, message, type });
  };

  const loadMensajes = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await contactoService.obtenerMensajes();
      setMensajes(data);
      // Sincronizar stats con los mensajes reales cargados
      refreshStats();
    } catch (error) {
      showToast('Error', 'No se pudieron cargar los mensajes', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [refreshStats]);

  useEffect(() => {
    loadMensajes();
  }, [loadMensajes]);

  const openDrawer = (mensaje: ContactoMensaje) => {
    setSelectedMensaje(mensaje);
    setIsDrawerOpen(true);
    setTimeout(() => setIsDrawerVisible(true), 10);
  };

  const closeDrawer = () => {
    setIsDrawerVisible(false);
    setTimeout(() => {
      setIsDrawerOpen(false);
      setSelectedMensaje(null);
    }, 300);
  };

  const handleMarcarLeido = async (id: string) => {
    try {
      await contactoService.marcarComoLeido(id);
      setMensajes(prev =>
        prev.map(m => (m.id === id ? { ...m, estado: 'leido' as const } : m))
      );
      if (selectedMensaje?.id === id) {
        setSelectedMensaje(prev => (prev ? { ...prev, estado: 'leido' as const } : null));
      }
      decrementNuevos();
      showToast('Éxito', 'Mensaje marcado como leído', 'success');
    } catch (error) {
      showToast('Error', 'No se pudo marcar el mensaje', 'error');
    }
  };

  const mensajesFiltrados = mensajes.filter(m => {
    if (filtro === 'todos') return true;
    return m.estado === filtro;
  });

  const totalNuevos = mensajes.filter(m => m.estado === 'nuevo').length;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <p className="text-xs sm:text-xs text-gray-500">
              {totalNuevos > 0 ? (
                <span className="text-red-600 font-medium">{totalNuevos} mensaje{totalNuevos !== 1 ? 's' : ''} nuevo{totalNuevos !== 1 ? 's' : ''}</span>
              ) : (
                'No hay mensajes nuevos'
              )}
            </p>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex items-center space-x-1 sm:space-x-2 bg-white rounded-lg border border-gray-200 p-1 self-start sm:self-auto">
          <Filter className="hidden sm:block w-4 h-4 text-gray-400 ml-2" />
          {(['todos', 'nuevo', 'leido'] as FiltroEstado[]).map((f) => (
            <button
              key={f}
              onClick={() => setFiltro(f)}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-xs font-medium transition-colors ${
                filtro === f
                  ? 'bg-purple-100 text-purple-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f === 'todos' ? 'Todos' : f === 'nuevo' ? 'Nuevos' : 'Leídos'}
            </button>
          ))}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : mensajesFiltrados.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <MessageSquare className="w-12 h-12 mb-4" />
            <p className="text-xs font-medium">No hay mensajes</p>
            <p className="text-xs">Los mensajes de contacto aparecerán aquí</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Remitente</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Contacto</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Asunto</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Mensaje</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {mensajesFiltrados.map((mensaje) => (
                  <tr
                    key={mensaje.id}
                    onClick={() => openDrawer(mensaje)}
                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${
                      mensaje.estado === 'nuevo' ? 'bg-red-50/30' : ''
                    }`}
                  >
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <span
                        className={`inline-flex items-center px-1.5 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                          mensaje.estado === 'nuevo'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {mensaje.estado === 'nuevo' ? 'Nuevo' : 'Leído'}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <div className="flex items-center space-x-1.5 sm:space-x-2">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center shrink-0">
                          <span className="text-white text-[10px] sm:text-xs font-medium">
                            {mensaje.nombreCompleto.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="text-xs sm:text-xs font-medium text-gray-900">{mensaje.nombreCompleto}</span>
                      </div>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <div className="space-y-0.5 sm:space-y-1">
                        <div className="flex items-center space-x-1 text-xs sm:text-xs text-gray-600">
                          <Mail className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                          <span className="truncate max-w-[100px] sm:max-w-none">{mensaje.email}</span>
                        </div>
                        {mensaje.telefono && (
                          <div className="flex items-center space-x-1 text-xs sm:text-xs text-gray-500">
                            <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                            <span>{mensaje.telefono}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <span className="text-xs sm:text-xs text-gray-700">{mensaje.asunto}</span>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <p className="text-xs sm:text-xs text-gray-600 max-w-[120px] sm:max-w-xs truncate">{mensaje.mensaje}</p>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <div className="flex items-center space-x-1 text-xs sm:text-xs text-gray-500">
                        <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
                        <span className="whitespace-nowrap">{formatDate(mensaje.creadoEn)}</span>
                      </div>
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-right">
                      <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                        {mensaje.estado === 'nuevo' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarcarLeido(mensaje.id);
                            }}
                            className="p-1 sm:p-1.5 rounded-lg hover:bg-purple-50 text-purple-600 transition-colors"
                            title="Marcar como leído"
                          >
                            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drawer de detalle */}
      {isDrawerOpen && selectedMensaje && (
        <div className="fixed left-0 right-0 bottom-0 top-0 z-[60] overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            {/* Overlay */}
            <div
              className={`absolute inset-0 transition-opacity duration-300 ${
                isDrawerVisible ? 'opacity-100' : 'opacity-0'
              }`}
              onClick={closeDrawer}
            />

            {/* Drawer panel */}
            <section
              className={`absolute right-0 top-0 h-full w-full max-w-lg border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
                isDrawerVisible ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              <div className="h-full flex flex-col bg-white shadow-xl">
                {/* Header */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-100 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-xs sm:text-xs font-medium text-gray-900 truncate">{selectedMensaje.nombreCompleto}</h3>
                        <span
                          className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                            selectedMensaje.estado === 'nuevo'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {selectedMensaje.estado === 'nuevo' ? 'Nuevo' : 'Leído'}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={closeDrawer}
                      className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Email</label>
                      <p className="text-xs sm:text-xs text-gray-900 mt-1 break-all">{selectedMensaje.email}</p>
                    </div>
                    {selectedMensaje.telefono && (
                      <div>
                        <label className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Teléfono</label>
                        <p className="text-xs sm:text-xs text-gray-900 mt-1">{selectedMensaje.telefono}</p>
                      </div>
                    )}
                    <div>
                      <label className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Asunto</label>
                      <p className="text-xs sm:text-xs text-gray-900 mt-1">{selectedMensaje.asunto}</p>
                    </div>
                    <div>
                      <label className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Fecha</label>
                      <p className="text-xs sm:text-xs text-gray-900 mt-1">{formatDate(selectedMensaje.creadoEn)}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase">Mensaje</label>
                    <div className="mt-2 p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-xs sm:text-xs text-gray-700 whitespace-pre-wrap">{selectedMensaje.mensaje}</p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-2 sm:space-x-3">
                  <button
                    onClick={closeDrawer}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cerrar
                  </button>
                  {selectedMensaje.estado === 'nuevo' && (
                    <button
                      onClick={() => {
                        handleMarcarLeido(selectedMensaje.id);
                      }}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Marcar como leído
                    </button>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      )}

      {/* Toast */}
      <NotificationToast
        isOpen={toast.isOpen}
        title={toast.title}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default DashboardContactosPage;
